import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type {
  RemoteAnswer,
  RemoteCommandKind,
  RemoteCommandLogEntry,
  usePresentationRemoteController,
} from '../../hooks/usePresentationRemoteController';
import type {
  RemoteBgmPick,
  RemoteNoticeDurationSec,
  RemotePresenceState,
  RemoteQuestionType,
  RemoteQuestionMode,
  RemoteAnswerKey,
} from '../../types/presentationRemote';
import {
  QUESTION_TYPE_LABEL,
  REMOTE_QUESTION_TYPES,
  REMOTE_CHOICE_MAX,
  REMOTE_CHOICE_MAXLEN,
  REMOTE_CHOICE_MIN,
  answerKeyChoiceIndexes,
  judgeAnswer,
  normalizeQuestionType,
  splitShortKeys,
} from '../../utils/remoteQuestion';
import { BGM_MOOD_SECTIONS } from '../program/ProgramSlideBgmModal';
import { communicationChoiceSwatchStyle } from '../../utils/presentationCommunicationAnswers';
import { slideBgmTracksForMood } from '../../utils/slideBgmPlayback';
import RemoteTtsSettingsCard from './RemoteTtsSettingsCard';
import { RemoteFlatIcon, RemoteOptIcon } from './RemoteFlatIcons';
import { OxMark } from './RemotePresentationOverlays';
import './RemotePresentationOverlays.css';
import { hhmm, remoteEntryStatusText } from './remoteCommandLabels';
import RemoteCommRoomCard from './RemoteCommRoomCard';
import { COMM_MAX_STUDENTS } from '../../services/realtime/commClient';
import { usePushToTalk } from '../../hooks/usePushToTalk';
import './RemoteControllerChat.css';

type Ctl = ReturnType<typeof usePresentationRemoteController>;
type ComposeType = 'notice' | 'question' | 'caption';
type ItemState = RemoteCommandLogEntry['state'];
const TYPE_LABEL: Record<ComposeType, string> = { notice: '공지', question: '질문', caption: '자막' };
const MicIcon: React.FC<{ size?: number }> = ({ size = 22 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="9" y="3" width="6" height="11" rx="3" />
    <path d="M5 11a7 7 0 0 0 14 0M12 18v3" />
  </svg>
);

/** 대화창 항목 — 세션 동안만 보관(sessionStorage) */
interface ThreadItem {
  id: string;
  type: ComposeType;
  text: string;
  durationSec: RemoteNoticeDurationSec;
  speak: boolean;
  at: number;
  targetName: string;
  /** 보낸 곳 표시(로컬 프레젠테이션 / 개별 디바이스) — 이전 항목은 없음 */
  dest?: string;
  /** 보낸 대상 프레젠 id — 이전 항목은 없음(이름으로 매칭) */
  targetId?: string;
  state: ItemState;
  error?: string;
  requestId?: string;
  /** 질문 유형 — 이전 항목은 없음(=단답형) */
  questionType?: RemoteQuestionType;
  choices?: string[];
  /** 이전 항목은 없음(=제출모드) */
  qMode?: RemoteQuestionMode;
  answerKey?: RemoteAnswerKey;
  /** 정답모드: 프레젠에 정답 공개 — 없음=공개 */
  revealAnswer?: boolean;
  answers?: RemoteAnswer[];
  /** 프레젠에서 내려감(닫힘·교체·시간 만료) → '완료' */
  closed?: boolean;
  /** ACK 받은 시각 — 시간 지정 공지 만료 계산 */
  ackAt?: number;
}

const DURATIONS: { label: string; sec: RemoteNoticeDurationSec }[] = [
  { label: '5초', sec: 5 },
  { label: '10초', sec: 10 },
  { label: '30초', sec: 30 },
  { label: '무제한', sec: null },
];
const QUESTION_TYPES: RemoteQuestionType[] = REMOTE_QUESTION_TYPES;
/** 질문 설정(UI 환경설정만) — 마지막 설정 기억 */
const QPREFS_LS = 'cp-pres-remote-qprefs-v1';
type QPrefs = { qType: RemoteQuestionType; grade: boolean; reveal: boolean };
const QPREFS_DEFAULT: QPrefs = { qType: 'short', grade: false, reveal: true };
function loadQPrefs(): QPrefs {
  try {
    const v = JSON.parse(window.localStorage.getItem(QPREFS_LS) ?? 'null') as Partial<QPrefs> | null;
    if (!v) return QPREFS_DEFAULT;
    return {
      qType: normalizeQuestionType(v.qType),
      grade: v.grade === true,
      reveal: v.reveal !== false,
    };
  } catch {
    return QPREFS_DEFAULT;
  }
}
const THREAD_MAX = 60;

/** O/X 응답은 큰 평면 기호로 */
const OxGlyph: React.FC<{ kind: 'O' | 'X' }> = ({ kind }) => (
  <svg className={`cp-rc-ox cp-rc-ox--${kind === 'O' ? 'o' : 'x'}`} viewBox="0 0 100 100" aria-label={kind} role="img">
    {kind === 'O' ? (
      <circle cx="50" cy="50" r="33" fill="none" stroke="currentColor" strokeWidth="14" />
    ) : (
      <g stroke="currentColor" strokeWidth="14" strokeLinecap="round">
        <line x1="22" y1="22" x2="78" y2="78" />
        <line x1="78" y1="22" x2="22" y2="78" />
      </g>
    )}
  </svg>
);
const threadKey = (userId: string) => `cp-pres-remote-thread-v1:${userId}`;

function loadThread(userId: string): ThreadItem[] {
  try {
    const raw = window.sessionStorage.getItem(threadKey(userId));
    const arr = raw ? (JSON.parse(raw) as ThreadItem[]) : [];
    if (!Array.isArray(arr)) return [];
    const now = Date.now();
    const lastNotice = [...arr].reverse().find((t) => t.type === 'notice' && t.state === 'ok');
    return arr.map((t) => {
      // 새로고침으로 끊긴 전송 중 항목은 상태를 알 수 없음
      if (t.state === 'pending') return { ...t, state: 'fail', error: 'timeout' };
      if (t.state !== 'ok' || t.closed) return t;
      // 확인 가능한 경우(가장 최근 시간 지정 공지가 아직 표시 시간 내)만 적용중 유지, 나머지는 완료
      const stillTimed =
        t === lastNotice && t.durationSec != null && (t.ackAt ?? t.at) + t.durationSec * 1000 > now;
      // 질문은 컨트롤러 상태(ctl.question)로 다시 열릴 수 있음
      return stillTimed ? t : { ...t, closed: true };
    });
  } catch {
    return [];
  }
}

const randomId = () => `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** 화면 아래에서 올라오는 시트(패널 안 절대배치) */
const Sheet: React.FC<{
  title: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  label: string;
  size?: 'sm' | 'md' | 'lg';
  fit?: boolean;
}> = ({
  title,
  fit = false,
  size = 'sm',
  onClose,
  children,
  label,
}) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [onClose]);
  return (
    <div className={`cp-rc-sheet-layer${fit ? ' is-fit' : ''}`} onMouseDown={onClose}>
      <div className={`cp-rc-sheet cp-rc-sheet--${size}`} role="dialog" aria-label={label} onMouseDown={(e) => e.stopPropagation()}>
        <div className="cp-rc-sheet-head">
          <span className="cp-rc-sheet-title">{title}</span>
          <button type="button" className="cp-rc-iconbtn" onClick={onClose} aria-label="닫기">
            ×
          </button>
        </div>
        <div className="cp-rc-sheet-body">{children}</div>
      </div>
    </div>
  );
};


export interface RemoteControllerChatProps {
  ctl: Ctl;
  userId: string;
  onRename: (target: RemotePresenceState) => void;
  onOpenPaidGuide: () => void;
}

const RemoteControllerChat: React.FC<RemoteControllerChatProps> = ({ ctl, userId, onRename, onOpenPaidGuide }) => {
  const disabled = !ctl.selectedOnline;
  const latest = useCallback((k: RemoteCommandKind) => ctl.log.find((e) => e.kind === k), [ctl.log]);
  const pending = (k: RemoteCommandKind) => latest(k)?.state === 'pending';

  /* ---------------- thread ---------------- */
  const [thread, setThread] = useState<ThreadItem[]>(() => loadThread(userId));
  useEffect(() => {
    try {
      window.sessionStorage.setItem(threadKey(userId), JSON.stringify(thread.slice(-THREAD_MAX)));
    } catch {
      /* ignore */
    }
  }, [thread, userId]);
  const patch = useCallback((id: string, p: Partial<ThreadItem>) => {
    setThread((prev) => prev.map((t) => (t.id === id ? { ...t, ...p } : t)));
  }, []);

  /* 대화창은 선택한 대상 프레젠별 기록 */
  const selTargetId = ctl.selectedSessionId ?? null;
  const selTargetName = ctl.selectedPresentation?.name ?? '';
  const isForSel = useCallback(
    (t: ThreadItem) => (t.targetId ? t.targetId === selTargetId : !!selTargetName && t.targetName === selTargetName),
    [selTargetId, selTargetName]
  );
  const view = useMemo(() => thread.filter(isForSel), [thread, isForSel]);

  // 질문 requestId 연결 + 응답 스냅샷
  const q = ctl.question;
  useEffect(() => {
    if (!q) return;
    setThread((prev) => {
      let changed = false;
      const next = prev.map((t) => {
        if (t.type !== 'question') return t;
        if (!t.requestId && isForSel(t) && t.text === q.text && t.state !== 'fail' && !prev.some((x) => x.requestId === q.requestId)) {
          changed = true;
          return { ...t, requestId: q.requestId };
        }
        if (t.requestId === q.requestId) {
          const closed = !q.open;
          const answers = ctl.answers.filter((a) => a.requestId === q.requestId);
          const sig = (l: RemoteAnswer[] | undefined) => (l ?? []).map((a) => a.key).join('|');
          if (t.closed !== closed || sig(t.answers) !== sig(answers)) {
            changed = true;
            return { ...t, closed, answers };
          }
        }
        return t;
      });
      return changed ? next : prev;
    });
  }, [q, ctl.answers, isForSel]);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const answerCount = view.reduce((n, t) => n + (t.answers?.length ?? 0), 0);
  // 새 항목·응답·ACK로 액션 줄이 생길 때마다 맨 아래까지(액션·안내 줄 포함) 보이게
  const lastLog = ctl.log[0];
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const raf = window.requestAnimationFrame(() => {
      el.scrollTop = el.scrollHeight;
    });
    return () => window.cancelAnimationFrame(raf);
  }, [view, answerCount, lastLog?.id, lastLog?.state]);

  const isActive = (t: ThreadItem) => t.state === 'ok' && !t.closed;
  const activeCaptionId = useMemo(
    () => [...view].reverse().find((t) => t.type === 'caption' && t.state === 'ok' && !t.closed)?.id,
    [view]
  );
  const activeNoticeId = useMemo(
    () => [...view].reverse().find((t) => t.type === 'notice' && t.state === 'ok' && !t.closed)?.id,
    [view]
  );
  const activeQuestionId = useMemo(
    () => (q?.open ? view.find((t) => t.requestId === q.requestId && !t.closed)?.id : undefined),
    [view, q]
  );

  /* ---------------- composer ---------------- */
  const [type, setType] = useState<ComposeType>('notice');
  const [text, setText] = useState('');
  /* 자막: 한 번 누르면 인식, 다시 누르거나 5초간 입력이 없으면 완료. 대화창에는 1회 = 카드 1개 */
  /** 보낸 곳 라벨: 소통 방 = 개별 디바이스, 그 외 = 로컬 프레젠테이션 (학생 기기는 '개별 디바이스' 대상일 때만) */
  const destLabel = (): string => {
    const sel = ctl.selectedPresentation;
    if (!sel) return '';
    return sel.presentationKind === 'comm' ? '개별 디바이스' : '로컬 프레젠테이션';
  };
  const ptt = usePushToTalk((id, body, final) => {
    const ok = ctl.sendCaption(id, body, final);
    setThread((prev) => {
      const has = prev.some((t) => t.id === id);
      if (has) return prev.map((t) => (t.id === id ? { ...t, text: body, state: ok ? 'ok' : t.state } : t));
      const item: ThreadItem = {
        id,
        type: 'caption',
        text: body,
        durationSec: null,
        speak: false,
        at: Date.now(),
        targetName: ctl.selectedPresentation?.name ?? '',
        dest: destLabel(),
        targetId: ctl.selectedSessionId ?? undefined,
        state: ok ? 'ok' : 'fail',
        error: ok ? undefined : 'no_target',
      };
      /* 새 자막이 이전 자막을 교체 → 이전 것은 완료 */
      const closedPrev = prev.map((t) =>
        t.type === 'caption' && !t.closed && (t.targetId ?? '') === (item.targetId ?? '') ? { ...t, closed: true } : t
      );
      return [...closedPrev, item].slice(-THREAD_MAX);
    });
  });
  const pttStop = ptt.stop;
  useEffect(() => {
    if (type !== 'caption') pttStop();
  }, [type, pttStop]);
  const [duration, setDuration] = useState<RemoteNoticeDurationSec>(10);
  const [speak, setSpeak] = useState(false);
  const initialQPrefs = useMemo(loadQPrefs, []);
  const [qType, setQType] = useState<RemoteQuestionType>(initialQPrefs.qType);
  const newChoice = () => ({ id: randomId(), text: '' });
  const [choices, setChoices] = useState<{ id: string; text: string }[]>(() => [newChoice(), newChoice()]);
  const [qMode, setQMode] = useState<RemoteQuestionMode>(initialQPrefs.grade ? 'answer' : 'submit');
  /** 정답 표시는 보기 id로 → 순서를 바꿔도 따라감 */
  /** 선택형 정답(복수 가능) — 보기 id 목록 */
  const [keyChoiceIds, setKeyChoiceIds] = useState<string[]>([]);
  const [keyOx, setKeyOx] = useState<'O' | 'X' | null>(null);
  const [keyText, setKeyText] = useState('');
  const [revealKey, setRevealKey] = useState(initialQPrefs.reveal);
  useEffect(() => {
    try {
      window.localStorage.setItem(QPREFS_LS, JSON.stringify({ qType, grade: qMode === 'answer', reveal: revealKey }));
    } catch {
      /* ignore */
    }
  }, [qType, qMode, revealKey]);
  /* 예전 '화면 + 학생 기기 동시 보내기' 설정 — 기능 삭제(학생 기기는 '개별 디바이스' 대상으로만), 남은 저장값 정리 */
  useEffect(() => {
    try {
      window.localStorage.removeItem('cp-remote-q-also-students');
    } catch {
      /* ignore */
    }
  }, []);
  /* 예전 '열 팝업(질문 페이지/리모트 질문)' 설정 — 기능 삭제, 남은 기기 저장값 정리 */
  useEffect(() => {
    try {
      window.localStorage.removeItem('cp-remote-q-lesson-popup');
    } catch {
      /* ignore */
    }
  }, []);
  /* 질문 설정 팝오버 */
  const [qPopOpen, setQPopOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(() => new Set());
  const toggleExpanded = (id: string) =>
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const qPopRef = useRef<HTMLDivElement | null>(null);
  const qChipRef = useRef<HTMLButtonElement | null>(null);
  useEffect(() => {
    if (!qPopOpen) return undefined;
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (qPopRef.current?.contains(t) || qChipRef.current?.contains(t)) return;
      setQPopOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // window 캡처 단계에서 먼저 받아 모달의 Esc 닫기로 번지지 않게 함
      e.preventDefault();
      e.stopImmediatePropagation();
      setQPopOpen(false);
      qChipRef.current?.focus();
    };
    document.addEventListener('pointerdown', onDown, true);
    window.addEventListener('keydown', onKey, true);
    const raf = window.requestAnimationFrame(() =>
      qPopRef.current?.querySelector<HTMLButtonElement>('.cp-rc-qpop-type.is-on')?.focus()
    );
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      window.removeEventListener('keydown', onKey, true);
      window.cancelAnimationFrame(raf);
    };
  }, [qPopOpen]);
  const [keyChips, setKeyChips] = useState<string[]>([]);
  /** 입력 중 텍스트를 칩으로 확정(쉼표 분리·중복 제거) */
  const commitKeyText = (raw: string) => {
    const parts = splitShortKeys(raw);
    if (parts.length) setKeyChips((prev) => [...prev, ...parts.filter((x) => !prev.includes(x))].slice(0, 5));
    setKeyText('');
  };
  const shortKeys = [...keyChips, ...splitShortKeys(keyText).filter((x) => !keyChips.includes(x))].slice(0, 5);
  const isChoice = type === 'question' && qType === 'choice';
  /* 서술형은 정답 채점 없음 → 항상 제출만 */
  const isEssay = qType === 'essay';
  const isAnswerMode = type === 'question' && qMode === 'answer' && !isEssay;
  const filled = choices.filter((c) => c.text.trim());
  const filledChoices = filled.map((c) => c.text.trim());
  const choiceError =
    isChoice && filledChoices.length < REMOTE_CHOICE_MIN ? `보기를 ${REMOTE_CHOICE_MIN}개 이상 입력하세요` : '';
  const answerKey: RemoteAnswerKey | null = !isAnswerMode
    ? null
    : qType === 'ox'
      ? keyOx
        ? { ox: keyOx }
        : null
      : qType === 'choice'
        ? (() => {
            const idx = filled.map((c, i) => (keyChoiceIds.includes(c.id) ? i : -1)).filter((i) => i >= 0);
            if (!idx.length) return null;
            return idx.length > 1 ? { choiceIndex: idx[0], choiceIndexes: idx } : { choiceIndex: idx[0] };
          })()
        : shortKeys.length
          ? { texts: shortKeys }
          : null;
  const keyError = isAnswerMode && !answerKey ? '정답을 표시하세요' : '';
  const sending = type === 'caption' ? false : pending(type);
  const canSend = type !== 'caption' && !disabled && !!text.trim() && !sending && !choiceError && !keyError;

  /* 보기 드래그 정렬 (포인터 — 마우스·터치 공통) */
  const choiceRowRefs = useRef<Map<string, HTMLDivElement>>(new Map());
  const [dragId, setDragId] = useState<string | null>(null);
  const onHandleDown = (id: string) => (e: React.PointerEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.currentTarget.setPointerCapture(e.pointerId);
    setDragId(id);
  };
  const onHandleMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!dragId) return;
    const y = e.clientY;
    setChoices((prev) => {
      const from = prev.findIndex((c) => c.id === dragId);
      if (from < 0) return prev;
      let to = from;
      prev.forEach((c, i) => {
        const r = choiceRowRefs.current.get(c.id)?.getBoundingClientRect();
        if (!r) return;
        if (i < from && y < r.top + r.height / 2) to = Math.min(to, i);
        if (i > from && y > r.top + r.height / 2) to = Math.max(to, i);
      });
      if (to === from) return prev;
      const next = [...prev];
      const [m] = next.splice(from, 1);
      next.splice(to, 0, m);
      return next;
    });
  };
  const onHandleUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
    setDragId(null);
  };
  const moveChoice = (id: string, d: -1 | 1) =>
    setChoices((prev) => {
      const i = prev.findIndex((c) => c.id === id);
      const j = i + d;
      if (i < 0 || j < 0 || j >= prev.length) return prev;
      const next = [...prev];
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });

  const send = async () => {
    if (!canSend) return;
    const body = text.trim().slice(0, type === 'notice' ? 500 : 300);
    const item: ThreadItem = {
      id: randomId(),
      type,
      text: body,
      durationSec: type === 'notice' ? duration : null,
      speak,
      at: Date.now(),
      targetName: ctl.selectedPresentation?.name ?? '',
      dest: destLabel(),
      targetId: ctl.selectedSessionId ?? undefined,
      state: 'pending',
      ...(type === 'question'
        ? {
            questionType: qType,
            ...(qType === 'choice' ? { choices: filledChoices } : {}),
            qMode: answerKey ? ('answer' as const) : ('submit' as const),
            ...(answerKey ? { answerKey, revealAnswer: revealKey } : {}),
          }
        : {}),
    };
    setThread((prev) => [...prev, item].slice(-THREAD_MAX));
    setText('');
    if (isChoice) setChoices([newChoice(), newChoice()]);
    if (type === 'question') {
      setKeyChoiceIds([]);
      setKeyOx(null);
      setKeyText('');
      setKeyChips([]);
    }
    const ok =
      type === 'notice'
        ? await ctl.sendNotice(body, duration, speak)
        : await ctl.askQuestion(body, speak, {
            questionType: qType,
            choices: qType === 'choice' ? filledChoices : undefined,
            mode: answerKey ? 'answer' : 'submit',
            answerKey: answerKey ?? undefined,
            revealAnswer: revealKey,
          });
    const ackAt = Date.now();
    setThread((prev) =>
      prev.map((t) =>
        t.id === item.id
          ? { ...t, state: ok ? 'ok' : 'fail', ackAt: ok ? ackAt : undefined }
          : ok && t.type === item.type && t.state === 'ok' && !t.closed && (t.targetId ?? '') === (item.targetId ?? '')
            ? { ...t, closed: true } // 한 번에 하나만 표시 → 이전 항목은 교체되어 완료
            : t
      )
    );
  };

  const closeActive = (kind: ComposeType) =>
    setThread((prev) => prev.map((t) => (t.type === kind && t.state === 'ok' && !t.closed && isForSel(t) ? { ...t, closed: true } : t)));
  const onCloseNotice = async () => {
    if (await ctl.closeNotice()) closeActive('notice');
  };
  const onCloseQuestion = async () => {
    await ctl.closeQuestion();
    closeActive('question');
  };

  // 시간 지정 공지: ACK 시각 + 표시 시간이 지나면 완료
  const timedActive = view.find(
    (t) => t.type === 'notice' && t.state === 'ok' && !t.closed && t.durationSec != null
  );
  useEffect(() => {
    if (!timedActive || timedActive.durationSec == null) return undefined;
    const left = (timedActive.ackAt ?? timedActive.at) + timedActive.durationSec * 1000 - Date.now();
    const id = window.setTimeout(() => patch(timedActive.id, { closed: true }), Math.max(0, left));
    return () => window.clearTimeout(id);
  }, [timedActive, patch]);
  // 실패 사유는 같은 종류의 최신 로그에서 가져옴
  useEffect(() => {
    setThread((prev) => {
      let changed = false;
      const next = prev.map((t) => {
        if (t.state !== 'fail' || t.error) return t;
        const e = ctl.log.find((l) => l.kind === t.type && l.state === 'fail' && l.at >= t.at);
        if (!e?.error) return t;
        changed = true;
        return { ...t, error: e.error };
      });
      return changed ? next : prev;
    });
  }, [ctl.log]);

  const fineInput = useMemo(
    () => typeof window !== 'undefined' && !!window.matchMedia?.('(hover: hover) and (pointer: fine)').matches,
    []
  );
  const taRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    const el = taRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 140)}px`;
  }, [text]);

  /* ---------------- music ---------------- */
  const [pick, setPick] = useState<{ pick?: RemoteBgmPick; label: string } | null>(null);
  /** 리모컨 자신의 마지막 음악 버튼(상태 동기화 없음) — 일시정지 뒤 재생은 이어서, 정지/처음은 고른 곡 새로 */
  const lastMusicActionRef = useRef<'play' | 'pause' | 'stop' | null>(null);
  const onMusicPlay = () => {
    const last = lastMusicActionRef.current;
    lastMusicActionRef.current = 'play';
    if (last === 'pause' || !pick?.pick) return void ctl.musicPlay(undefined, '음악 이어서 재생');
    void ctl.musicPlay(pick.pick, `${pick.label} 재생`, last !== 'play');
  };
  const onMusicPause = () => {
    lastMusicActionRef.current = 'pause';
    void ctl.musicPause();
  };
  const onMusicStop = () => {
    lastMusicActionRef.current = 'stop';
    void ctl.musicStop();
  };
  const [musicOpen, setMusicOpen] = useState(false);
  const music = latest('music');
  const playPick = (p: RemoteBgmPick, label: string) => {
    setPick({ pick: p, label });
    lastMusicActionRef.current = 'play';
    void ctl.musicPlay(p, label, true);
  };

  /* ---------------- sheets ---------------- */
  const [targetOpen, setTargetOpen] = useState(false);
  useEffect(() => {
    ctl.setTargetPickerOpen(targetOpen);
  }, [ctl.setTargetPickerOpen, targetOpen]);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const remoteCount = ctl.presentations.filter((p) => !p.isLocal).length;
  /** 수업 프레젠은 로컬 전용 → 온라인 관련 안내 숨김 */
  const selIsLesson = ctl.selectedPresentation?.presentationKind === 'lesson';
  /** 질문 설정 요약 (아이콘 버튼의 툴팁·스크린리더용 전체 문구) */
  const qSumLabel =
    (isEssay ? '채점 없음(서술형)' : qMode === 'answer' ? `채점 · ${revealKey ? '정답 공개' : '정답 비공개'}` : '채점 없음(제출만)') +
    (ctl.commSelected ? ' · 학생 기기로 보냄(개별 디바이스)' : ' · 학생 기기는 개별 디바이스 대상에서만');
  const sel = ctl.selectedPresentation;
  /* 소통 프레젠테이션(학생 기기) 대상: 공지·질문만 — 음악·읽어주기·슬라이드 숨김 */
  const selIsComm = ctl.commSelected;
  const comm = ctl.comm;
  const [commSheet, setCommSheet] = useState(false);
  const commOnline = comm.members.filter((m) => m.online).length;
  const commDefaultClass =
    ctl.presentations.find((p) => p.presentationKind === 'lesson' && p.title)?.title ??
    (() => {
      try {
        return window.sessionStorage.getItem('cp-comm-prefill-title') ?? '';
      } catch {
        return '';
      }
    })();
  /* 만든 직후 소통 대상으로 자동 선택 (시트는 공유 정보로 바뀜) */
  const selectCommOnCreateRef = useRef(false);
  const commTargetId = comm.targetId;
  const selectTarget = ctl.select;
  useEffect(() => {
    if (commTargetId && selectCommOnCreateRef.current) {
      selectCommOnCreateRef.current = false;
      selectTarget(commTargetId);
    }
  }, [commTargetId, selectTarget]);
  const pickComm = () => {
    if (!comm.canUse) {
      onOpenPaidGuide();
      return;
    }
    setTargetOpen(false);
    if (comm.room && comm.targetId) ctl.select(comm.targetId);
    else setCommSheet(true);
  };
  const ttsEntry = latest('tts');

  const statusChip = (s: ItemState, error?: string) => (
    <span className={`cp-rc-ack is-${s}`}>{remoteEntryStatusText({ state: s, error: error as RemoteCommandLogEntry['error'] })}</span>
  );

  return (
    <div className="cp-rc">
      {/* 상단: 대상 선택 · 온라인 찾기 · 설정 */}
      <div className="cp-rc-top">
        <button
          type="button"
          className="cp-rc-target"
          onClick={() => setTargetOpen(true)}
          aria-haspopup="dialog"
          aria-label="제어할 프레젠테이션 선택"
        >
          <RemoteFlatIcon kind="screen" size={22} />
          <span className="cp-rc-target-name">
            {sel ? sel.name : ctl.presentations.length ? '프레젠테이션 선택' : '열린 프레젠테이션 없음'}
            {sel ? (
              <small className={ctl.selectedOnline ? 'is-online' : 'is-offline'}>
                {selIsComm
                  ? ctl.selectedOnline
                    ? `● 학생 ${comm.members.length}/${COMM_MAX_STUDENTS}`
                    : comm.status === 'disconnected' || comm.status === 'error'
                      ? '연결 끊김'
                      : '연결 중…'
                  : ctl.selectedOnline
                  ? sel.presentationKind === 'lesson'
                    ? sel.isLocal
                      ? '● 수업 프레젠 · 로컬'
                      : '● 수업 프레젠 · 온라인'
                    : sel.isLocal
                      ? '● 로컬 · 이 기기'
                      : '● 온라인'
                  : '오프라인'}
              </small>
            ) : null}
          </span>
          <RemoteFlatIcon kind="chevron" size={18} />
        </button>
        {selIsComm ? (
          <button type="button" className="cp-rc-iconbtn cp-rc-iconbtn--soft cp-rc-comm-info" onClick={() => setCommSheet(true)} aria-label="개별 디바이스 프레젠테이션 정보(링크·입장번호)" title="링크·입장번호">
            i
          </button>
        ) : null}
        {selIsComm && comm.room && (comm.status === 'disconnected' || comm.status === 'error') ? (
          <button type="button" className="cp-remote-reconnect-btn cp-rc-comm-reconnect" onClick={comm.reconnect}>
            다시 연결
          </button>
        ) : null}
        {selIsComm ? null : (
          <button type="button" className="cp-rc-iconbtn cp-rc-iconbtn--soft" onClick={() => setSettingsOpen(true)} aria-label="읽어주기 설정">
            <RemoteFlatIcon kind="gear" size={22} />
          </button>
        )}
      </div>

      {!selIsLesson && !selIsComm && ctl.status === 'limited' ? (
        <p className="cp-rc-banner is-warn" role="status">
          지금은 원격 연결이 혼잡해요. 같은 브라우저의 프레젠은 계속 제어할 수 있어요.
        </p>
      ) : null}
      {!selIsLesson && !selIsComm && ctl.remoteBlockedByPlan ? (
        <div className="cp-rc-banner is-warn" role="status">
          <span>다른 기기 연결은 유료 플랜 전용이에요. 같은 브라우저의 프레젠은 계속 제어할 수 있어요.</span>
          <button type="button" className="cp-rc-banner-btn" onClick={onOpenPaidGuide}>
            이용권 안내
          </button>
        </div>
      ) : null}
      {!selIsLesson && !selIsComm && ctl.remoteActive && ctl.status === 'connected' && remoteCount === 0 ? (
        <p className="cp-rc-banner cp-rc-banner--other-devices">다른 기기가 안 보이면 메인 프레젠테이션 하단의 ‘원격 제어’를 켜 주세요. 켜 두면 1시간 후 자동으로 꺼집니다.</p>
      ) : null}

      {/* 대화창 */}
      <div className="cp-rc-thread-wrap">
      <div className="cp-rc-thread" ref={scrollRef} aria-live="polite">
        {view.length === 0 ? (
          <div className="cp-rc-empty">
            <RemoteFlatIcon kind="notice" size={40} />
            <p>
              {ctl.presentations.length === 0
                ? '열려 있는 메인 프레젠테이션이 없어요.\n같은 계정으로 PC에서 메인 프레젠테이션을 열어 주세요.'
                : '아래에서 공지나 질문을 보내면\n여기에 기록돼요.'}
            </p>
          </div>
        ) : (
          view.map((t) => {
            const isDone = t.state === 'ok' && !!t.closed;
            const collapsed = isDone && !expandedIds.has(t.id);
            const foldable = isDone && t.type === 'question';
            const foldProps = foldable
              ? {
                  onClick: (e: React.MouseEvent) => {
                    if ((e.target as HTMLElement).closest('button, a, input, textarea, select, [data-no-fold]') && !(e.target as HTMLElement).closest('.cp-rc-sum')) return;
                    toggleExpanded(t.id);
                  },
                }
              : {};
            return (
            <React.Fragment key={t.id}>
              <div
                className={`cp-rc-bubble cp-rc-bubble--${t.type}${isDone ? ' is-done' : ''}${collapsed ? ' is-collapsed' : ''}${
                  t.state === 'fail' ? ' is-fail' : ''
                }${isActive(t) ? ' is-live' : ''}${foldable ? ' is-foldable' : ''}`}
                {...foldProps}
                {...(foldable
                  ? {
                      role: 'button',
                      tabIndex: 0,
                      'aria-expanded': !collapsed,
                      onKeyDown: (e: React.KeyboardEvent) => {
                        if (e.target !== e.currentTarget) return;
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          toggleExpanded(t.id);
                        }
                      },
                    }
                  : {})}
              >
                <div className="cp-rc-card-head">
                  <span className="cp-rc-bubble-meta">
                    {t.type === 'caption' ? <MicIcon size={14} /> : <RemoteFlatIcon kind={t.type} size={16} />}
                    {TYPE_LABEL[t.type]}
                    {t.dest ? <span className="cp-rc-card-dest">· {t.dest}</span> : null}
                    {t.type === 'question' && t.qMode === 'answer' && t.revealAnswer === false ? (
                      <span className="cp-rc-hidden-key cp-rc-hidden-key--head">정답 비공개</span>
                    ) : null}
                  </span>
                  <span className="cp-rc-card-status">
                    <time className="cp-rc-card-time">{hhmm(t.at)}</time>
                  {t.state === 'ok' ? (
                    t.closed ? (
null
                    ) : (
                      <span className="cp-rc-ack is-live">
                        <span className="cp-rc-live-dot" aria-hidden />
                        {t.type === 'question' ? '응답 받는 중' : t.type === 'caption' ? (ptt.holding && t.id === activeCaptionId ? '말하는 중' : '표시 중') : '표시 중'}
                      </span>
                    )
                  ) : (
                    statusChip(t.state, t.error)
                  )}
                  </span>
                </div>
                <span className="cp-rc-bubble-text">{t.text}</span>
                {t.questionType === 'choice' && t.choices?.length ? (
                  // 프레젠 팝업과 같은 보기 타일(표시 전용)
                  <div className={`cp-remote-ov-choices cp-rc-card-choices${t.choices.length > 4 ? ' is-many' : ''}`} aria-label="보기">
                    {t.choices.map((c, i) => {
                      const isKey = t.qMode === 'answer' && answerKeyChoiceIndexes(t.answerKey, t.choices?.length ?? 0).includes(i);
                      return (
                        <span key={i} className={`cp-remote-ov-choice${isKey ? ' is-on' : ''}`} aria-label={isKey ? `정답 ${i + 1}. ${c}` : `${i + 1}. ${c}`}>
                          <span className="cp-remote-ov-choice-num cp-choice-swatch" style={communicationChoiceSwatchStyle(i)}>{i + 1}</span>
                          <span className="cp-remote-ov-choice-text">{c}</span>
                        </span>
                      );
                    })}
                  </div>
                ) : null}
                {t.questionType === 'ox' ? (
                  <div className="cp-remote-ov-ox cp-rc-card-ox" aria-label="O 또는 X">
                    {(['O', 'X'] as const).map((k) => {
                      const isKey = t.qMode === 'answer' && t.answerKey?.ox === k;
                      return (
                        <span
                          key={k}
                          className={`cp-remote-ov-ox-btn cp-remote-ov-ox-btn--${k === 'O' ? 'o' : 'x'}${isKey ? ' is-on' : ''}`}
                          aria-label={isKey ? `정답 ${k}` : k}
                        >
                          <OxMark kind={k} />
                        </span>
                      );
                    })}
                  </div>
                ) : null}
                {t.qMode === 'answer' && (t.questionType ?? 'short') === 'short' && t.answerKey ? (
                  <span className="cp-rc-bubble-key">
                    <span className="cp-rc-key-tag">정답</span>
                    {(t.answerKey.texts ?? []).map((k) => (
                      <span key={k} className="cp-rc-keychip cp-rc-keychip--bubble">
                        {k}
                      </span>
                    ))}
                  </span>
                ) : null}
              </div>
              {t.type === 'question' && t.state === 'ok'
                ? (() => {
                    // 같은 학생의 다시 답하기는 최신 답으로 교체
                    const bySender = new Map<string, NonNullable<typeof t.answers>[number]>();
                    for (const a of t.answers ?? []) {
                      // 소통: 학생당 하나(수정 답변이 교체) · 메인/수업 프레젠: 추가 답변 누적
                      const who = t.targetId?.startsWith('comm:') ? a.sessionId || a.presentationName : a.key;
                      const prev = bySender.get(who);
                      if (!prev || a.answeredAt >= prev.answeredAt) bySender.set(who, a);
                    }
                    const list = Array.from(bySender.values()).sort((a, b) => a.answeredAt - b.answeredAt);
                    const judged = list.map((a) =>
                      t.qMode === 'answer' ? judgeAnswer(t.questionType ?? 'short', t.answerKey, a.content, t.choices) : null,
                    );
                    const hasKey = judged.some((v) => v != null);
                    const correct = judged.filter((v) => v === true).length;
                    const total = selIsComm ? comm.members.length : null;
                    if (collapsed) {
                      return (
                        <div className="cp-rc-sum is-foldable" {...foldProps} aria-hidden="true">
                          <span>
                            응답 {list.length}
                            {hasKey ? ` · 정답 ${correct}` : ''}
                          </span>
                          <b>펼치기 ›</b>
                        </div>
                      );
                    }
                    return (
                      <div className={`cp-rc-responses${foldable ? ' is-foldable' : ''}`} aria-label="학생 응답" {...foldProps}>
                        <div className="cp-rc-responses-head">
                          응답 {list.length}
                          {total ? `/${total}` : ''}
                          {hasKey ? ` · 정답 ${correct}` : ''}
                        </div>
                        {list.length ? (
                          <div className="cp-rc-responses-grid">
                            {list.map((a, idx) => {
                              const v = judged[idx];
                              return (
                                <div
                                  key={a.key}
                                  className={`cp-rc-resp${v === true ? ' is-correct' : v === false ? ' is-wrong' : ''}`}
                                  title={`${a.presentationName} · ${hhmm(a.answeredAt)}`}
                                >
                                  <span className="cp-rc-resp-name">
                                    {a.presentationName}
                                  </span>
                                  {v == null ? null : (
                                    <span className={`cp-rc-mark ${v ? 'is-o' : 'is-x'}`} aria-label={v ? '정답' : '오답'}>{v ? '✓' : '✕'}</span>
                                  )}
                                  {t.questionType === 'ox' && (a.content === 'O' || a.content === 'X') ? (
                                    <span className="cp-rc-resp-text cp-rc-answer-text--ox">
                                      <OxGlyph kind={a.content} />
                                    </span>
                                  ) : (
                                    <span className="cp-rc-resp-text">{a.content}</span>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        ) : (
                          <p className="cp-rc-responses-empty">아직 응답이 없어요</p>
                        )}
                      </div>
                    );
                  })()
                : null}
              {t.id === activeCaptionId && !(ptt.holding && type === 'caption') ? (
                <div className="cp-rc-actions">
                  {selIsComm ? null : (
                    <button
                      type="button"
                      className="cp-rc-chip"
                      disabled={disabled || pending('caption') || !t.text.trim()}
                      onClick={() => void ctl.speakCurrent('caption', t.text)}
                    >
                      <RemoteFlatIcon kind="speaker" size={18} />
                      자막 읽기
                    </button>
                  )}
                  <button
                    type="button"
                    className="cp-rc-chip cp-rc-end-q"
                    disabled={disabled}
                    onClick={() => {
                      if (ctl.closeCaption(t.id)) closeActive('caption');
                    }}
                  >
                    자막 닫기
                  </button>
                </div>
              ) : null}
              {t.id === activeNoticeId ? (
                <div className="cp-rc-actions">
                  {selIsComm ? null : (
                  <button type="button" className="cp-rc-chip" disabled={disabled || pending('notice')} onClick={() => void ctl.speakCurrent('notice')}>
                    <RemoteFlatIcon kind="speaker" size={18} />
                    다시 읽기
                  </button>
                  )}
                  <button type="button" className="cp-rc-chip cp-rc-end-q" disabled={disabled || pending('notice')} onClick={() => void onCloseNotice()}>
                    공지 닫기
                  </button>
                </div>
              ) : null}
              {t.id === activeQuestionId ? (
                <div className="cp-rc-actions">
                  {selIsComm ? null : (
                  <button type="button" className="cp-rc-chip" disabled={disabled || pending('question')} onClick={() => void ctl.speakCurrent('question')}>
                    <RemoteFlatIcon kind="speaker" size={18} />
                    다시 읽기
                  </button>
                  )}
                  <button type="button" className="cp-rc-chip cp-rc-end-q" disabled={pending('question')} onClick={() => void onCloseQuestion()}>
                    질문 종료
                  </button>
                </div>
              ) : null}
            </React.Fragment>
            );
          })
        )}
        {(() => {
          const n = latest('notice');
          // 공지 닫기·다시 읽기 등 대화창 항목이 아닌 명령의 결과
          return n && !n.label.startsWith('공지 전송') ? (
            <p className={`cp-rc-sys is-${n.state}`}>
              {n.label}: {remoteEntryStatusText(n)}
            </p>
          ) : null;
        })()}
        {(() => {
          const n = latest('question');
          return n && (n.label === '응답 요청 종료' || n.label === '질문 다시 읽기') ? (
            <p className={`cp-rc-sys is-${n.state}`}>
              {n.label}: {remoteEntryStatusText(n)}
            </p>
          ) : null;
        })()}
        {(() => {
          const n = latest('caption');
          return n && n.label === '자막 읽기' ? (
            <p className={`cp-rc-sys is-${n.state}`}>
              {n.label}: {remoteEntryStatusText(n)}
            </p>
          ) : null;
        })()}
      </div>
      </div>

      {/* 컴포저 */}
      <div className={`cp-rc-composer${selIsComm ? ' is-comm' : ''}`}>
        <div className="cp-rc-composer-opts">
          <div className="cp-rc-seg" role="tablist" aria-label="보낼 종류">
            {(['notice', 'question', 'caption'] as const).map((k) => (
              <button
                key={k}
                type="button"
                role="tab"
                aria-selected={type === k}
                className={type === k ? 'is-on' : undefined}
                onClick={() => setType(k)}
              >
                <RemoteFlatIcon kind={k} tone={type === k ? 'white' : 'teal'} size={16} />
                {TYPE_LABEL[k]}
              </button>
            ))}
          </div>
          <div className={`cp-rc-composer-sub${type === 'caption' ? ' is-off' : ''}`}>
          {type === 'question' ? (
            <div className="cp-rc-typeseg" role="radiogroup" aria-label="질문 유형">
              {QUESTION_TYPES.map((k) => (
                <button key={k} type="button" role="radio" aria-checked={qType === k} className={qType === k ? 'is-on' : undefined} onClick={() => setQType(k)}>
                  {QUESTION_TYPE_LABEL[k]}
                </button>
              ))}
            </div>
          ) : null}
          {/* 같은 자리: 공지 = 표시 시간 / 질문 = 채점·공개 (전환 시 입력창 위치 고정) */}
          <div className="cp-rc-opt-slot">
          <div className={`cp-rc-durations cp-rc-qsum-wrap${type === 'question' ? '' : ' is-hidden'}`} aria-hidden={type !== 'question' || undefined}>
            <button
              ref={qChipRef}
              type="button"
              className={`cp-rc-qsum${qMode === 'answer' && !isEssay ? ' is-grade' : ''}`}
              aria-haspopup="dialog"
              aria-expanded={qPopOpen}
              tabIndex={type === 'question' ? 0 : -1}
              onClick={() => setQPopOpen((v) => !v)}
              title={qSumLabel}
              aria-label={`질문 설정: ${qSumLabel}`}
            >
              <span className="cp-rc-qsum-icons">
                <RemoteOptIcon
                  kind="grade"
                  on={qMode === 'answer' && !isEssay}
                  label={isEssay ? '채점 없음 (서술형은 채점하지 않아요)' : qMode === 'answer' ? '채점 켬' : '채점 없음 (제출만)'}
                />
                {qMode === 'answer' && !isEssay ? (
                  <RemoteOptIcon kind="eye" on={revealKey} label={revealKey ? '정답 공개' : '정답 비공개'} />
                ) : (
                  <RemoteOptIcon kind="eye" on={false} label="채점이 꺼져 있어 정답 공개 없음" />
                )}
                {selIsComm ? (
                  <RemoteOptIcon kind="phone" on label="학생 기기로 보내요 (개별 디바이스)" />
                ) : (
                  <RemoteOptIcon kind="phone" on={false} disabled label="학생 기기는 '개별 디바이스' 대상일 때만 보내요" />
                )}
              </span>
              <RemoteFlatIcon kind="chevron" tone={qMode === 'answer' && !isEssay ? 'white' : 'teal'} size={14} />
            </button>
          </div>
          <div
            className={`cp-rc-durations cp-rc-durseg${type === 'notice' ? '' : ' is-hidden'}`}
            role="radiogroup"
            aria-label="공지 표시 시간"
            aria-hidden={type !== 'notice' || undefined}
          >
            {DURATIONS.map((d) => (
              <button
                key={d.label}
                type="button"
                role="radio"
                aria-checked={duration === d.sec}
                className={duration === d.sec ? 'is-on' : undefined}
                tabIndex={type === 'notice' ? 0 : -1}
                onClick={() => setDuration(d.sec)}
              >
                {d.label}
              </button>
            ))}
          </div>
          </div>
          </div>
        </div>
        {type === 'question' && qPopOpen ? (
          <div ref={qPopRef} className="cp-rc-qpop" role="dialog" aria-label="질문 설정">
            <div className="cp-rc-qpop-types" role="radiogroup" aria-label="질문 유형">
              {QUESTION_TYPES.map((k) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={qType === k}
                  className={`cp-rc-qpop-type${qType === k ? ' is-on' : ''}`}
                  onClick={() => {
                    setQType(k);
                  }}
                >
                  {QUESTION_TYPE_LABEL[k]}
                </button>
              ))}
            </div>
            <label className={`cp-rc-qpop-row${isEssay ? ' is-disabled' : ''}`}>
              <span className="cp-rc-qpop-text">
                <b>정답 채점</b>
                <small>{isEssay ? '서술형은 정답 채점 없이 답만 받아요' : '제출하면 정답/오답을 바로 보여줘요'}</small>
              </span>
              <input
                type="checkbox"
                role="switch"
                className="cp-rc-qpop-switch"
                checked={qMode === 'answer' && !isEssay}
                aria-checked={qMode === 'answer' && !isEssay}
                disabled={isEssay}
                onChange={(e) => setQMode(e.target.checked ? 'answer' : 'submit')}
              />
            </label>
            <label className={`cp-rc-qpop-row${qMode === 'answer' && !isEssay ? '' : ' is-disabled'}`}>
              <span className="cp-rc-qpop-text">
                <b>학생에게 정답 공개</b>
                <small>틀렸을 때 정답을 화면에 보여줘요</small>
              </span>
              <input
                type="checkbox"
                role="switch"
                className="cp-rc-qpop-switch"
                checked={revealKey}
                aria-checked={revealKey}
                disabled={qMode !== 'answer' || isEssay}
                onChange={(e) => setRevealKey(e.target.checked)}
              />
            </label>
            <div className="cp-rc-qpop-foot">
              <span>마지막 설정 기억됨</span>
              <button
                type="button"
                onClick={() => {
                  setQType(QPREFS_DEFAULT.qType);
                  setQMode(QPREFS_DEFAULT.grade ? 'answer' : 'submit');
                  setRevealKey(QPREFS_DEFAULT.reveal);
                }}
              >
                기본값으로
              </button>
            </div>
          </div>
        ) : null}
        {type === 'caption' ? (
          <div className="cp-rc-ptt-wrap">
            <p className={`cp-rc-ptt-preview${ptt.preview ? ' has-text' : ''}`} aria-live="polite">
              {ptt.preview || (ptt.holding ? '듣는 중…' : '버튼을 누르면 말하기가 시작돼요. 다시 누르거나 5초간 말이 없으면 끝나요.')}
            </p>
            {!ptt.supported ? (
              <p className="cp-rc-ptt-msg">이 브라우저는 음성 인식을 지원하지 않아요 (Chrome/Safari 사용)</p>
            ) : ptt.error ? (
              <p className="cp-rc-ptt-msg">{ptt.error}</p>
            ) : null}
            <button
              type="button"
              className={`cp-rc-ptt${ptt.holding ? ' is-on' : ''}`}
              disabled={disabled || !ptt.supported}
              aria-pressed={ptt.holding}
              onClick={() => {
                if (ptt.holding) ptt.stop();
                else ptt.start();
              }}
            >
              <MicIcon size={26} />
              <span>{ptt.holding ? '말하는 중… 누르면 완료' : '눌러서 말하기'}</span>
            </button>
          </div>
        ) : null}
        {type !== 'caption' ? (
        <div className="cp-rc-input-row">
          <textarea
            ref={taRef}
            className="cp-rc-input"
            rows={1}
            value={text}
            maxLength={type === 'notice' ? 500 : 300}
            placeholder={
              disabled
                ? '먼저 제어할 프레젠테이션을 선택하세요'
                : type === 'notice'
                  ? '프레젠 화면에 띄울 공지'
                  : qType === 'ox'
                    ? 'OX 질문 입력 (예: 물은 끓는다)'
                    : qType === 'choice'
                      ? '질문 입력 (보기는 아래에)'
                      : qType === 'essay'
                        ? '서술형 질문 입력 (예: 그렇게 생각한 까닭을 써 보세요)'
                        : '질문 입력 (예: 오늘 배운 점은?)'
            }
            disabled={disabled}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && fineInput) {
                e.preventDefault();
                void send();
              }
            }}
          />
          {selIsComm ? null : (
          <button
            type="button"
            role="switch"
            aria-checked={speak}
            className={`cp-rc-speak${speak ? ' is-on' : ''}`}
            onClick={() => setSpeak(!speak)}
            aria-label="전송 시 읽어주기"
            title={speak ? '전송 시 읽어주기: 켜짐' : '전송 시 읽어주기: 꺼짐'}
          >
            <RemoteFlatIcon kind="speaker" tone={speak ? 'white' : 'teal'} size={22} />
            <span className="cp-rc-speak-label">
              {speak ? <span className="cp-rc-speak-dot" aria-hidden /> : null}
              읽기
            </span>
          </button>
          )}
          <button type="button" className="cp-rc-send" disabled={!canSend} onClick={() => void send()} aria-label={type === 'notice' ? '공지 보내기' : '질문 보내기'}>
            <RemoteFlatIcon kind="send" tone="white" size={26} />
          </button>
        </div>
        ) : null}
        {/* OX는 채점이 꺼져 있어도 O/X 정답 버튼을 보여 주고, 누르면 채점을 켠다(선택형 보기처럼 항상 보임) */}
        {(isAnswerMode && qType !== 'choice') || (type === 'question' && qType === 'ox') ? (
          <div className={`cp-rc-keycard${qType === 'ox' ? ' is-ox' : ''}`}>
            {qType === 'ox' ? (
              <div className="cp-rc-oxkey" role="radiogroup" aria-label="OX 정답">
                {(['O', 'X'] as const).map((k) => (
                  <button
                    key={k}
                    type="button"
                    role="radio"
                    aria-checked={isAnswerMode && keyOx === k}
                    aria-label={`정답 ${k}`}
                    className={`cp-rc-oxkey-btn cp-rc-oxkey-btn--${k === 'O' ? 'o' : 'x'}${isAnswerMode && keyOx === k ? ' is-key' : ''}`}
                    onClick={() => {
                      if (!isAnswerMode) {
                        setQMode('answer');
                        setKeyOx(k);
                        return;
                      }
                      if (keyOx === k) {
                        // 정답 해제 → 정답이 없으니 채점도 끔(자동 켜짐과 대칭)
                        setKeyOx(null);
                        setQMode('submit');
                      } else setKeyOx(k);
                    }}
                    title={isAnswerMode && keyOx === k ? '정답 해제' : `${k}를 정답으로 (채점 켜짐)`}
                  >
                    {isAnswerMode && keyOx === k ? <span className="cp-rc-key-tip">정답</span> : null}
                    <OxGlyph kind={k} />
                  </button>
                ))}
              </div>
            ) : null}
            {qType === 'short' ? (
              // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
              <div
                className="cp-rc-keychips"
                onMouseDown={(e) => {
                  const inp = (e.currentTarget as HTMLDivElement).querySelector('input');
                  if (inp && e.target !== inp && !(e.target as HTMLElement).closest('button')) {
                    e.preventDefault();
                    inp.focus();
                  }
                }}
              >
                {keyChips.map((k) => (
                  <span key={k} className="cp-rc-keychip">
                    {k}
                    <button
                      type="button"
                      className="cp-rc-keychip-x"
                      aria-label={`정답 ${k} 삭제`}
                      onClick={() => setKeyChips((prev) => prev.filter((x) => x !== k))}
                    >
                      ×
                    </button>
                  </span>
                ))}
                {keyChips.length < 5 ? (
                  <input
                    className="cp-rc-keychips-input"
                    value={keyText}
                    maxLength={100}
                    placeholder={keyChips.length ? '추가 정답' : '정답 입력 후 쉼표(,) 또는 Enter'}
                    aria-label="단답 정답 (쉼표로 구분)"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v.includes(',')) {
                        const lastComma = v.lastIndexOf(',');
                        commitKeyText(v.slice(0, lastComma));
                        setKeyText(v.slice(lastComma + 1).trimStart());
                      } else setKeyText(v);
                    }}
                    onPaste={(e) => {
                      const t = e.clipboardData.getData('text');
                      if (!t.includes(',')) return;
                      e.preventDefault();
                      commitKeyText(keyText + t);
                    }}
                    onKeyDown={(e) => {
                      if (e.nativeEvent.isComposing) return;
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        commitKeyText(keyText);
                      } else if (e.key === 'Backspace' && !keyText && keyChips.length) {
                        e.preventDefault();
                        setKeyChips((prev) => prev.slice(0, -1));
                      }
                    }}
                    onBlur={() => commitKeyText(keyText)}
                  />
                ) : null}
              </div>
            ) : null}
          </div>
        ) : null}
        {isChoice ? (
          <div className={`cp-rc-choices${dragId ? ' is-dragging' : ''}`} aria-label="보기">
            {choices.map((c, i) => {
              const isKey = isAnswerMode && keyChoiceIds.includes(c.id);
              return (
                <div
                  key={c.id}
                  ref={(el) => {
                    if (el) choiceRowRefs.current.set(c.id, el);
                    else choiceRowRefs.current.delete(c.id);
                  }}
                  className={`cp-rc-choice-row${dragId === c.id ? ' is-drag' : ''}${isKey ? ' is-key' : ''}`}
                >
                  <button
                    type="button"
                    className="cp-rc-choice-handle"
                    aria-label={`보기 ${i + 1} 순서 이동 (드래그 또는 위/아래 화살표)`}
                    onPointerDown={onHandleDown(c.id)}
                    onPointerMove={onHandleMove}
                    onPointerUp={onHandleUp}
                    onPointerCancel={onHandleUp}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
                        e.preventDefault();
                        moveChoice(c.id, e.key === 'ArrowUp' ? -1 : 1);
                      }
                    }}
                  >
                    <span aria-hidden>⋮⋮</span>
                  </button>
                  {/* 채점이 꺼져 있어도 번호를 누르면 채점을 켜고 그 번호를 정답으로(OX와 동일) */}
                  {isChoice ? (
                    <button
                      type="button"
                      className={`cp-rc-choice-num cp-choice-swatch is-pickable${isKey ? ' is-key' : ''}`}
                      style={communicationChoiceSwatchStyle(i)}
                      aria-pressed={isKey}
                      title={isKey ? '정답 해제' : isAnswerMode ? '정답으로 표시' : '정답으로 표시 (채점 켜짐)'}
                      onClick={() => {
                        if (!isAnswerMode) {
                          setQMode('answer');
                          setKeyChoiceIds([c.id]);
                          return;
                        }
                        // 복수 정답: 누를 때마다 정답에 넣고 빼기. 정답이 하나도 안 남으면 채점도 끔
                        if (isKey) {
                          const next = keyChoiceIds.filter((id) => id !== c.id);
                          setKeyChoiceIds(next);
                          if (!next.length) setQMode('submit');
                        } else setKeyChoiceIds([...keyChoiceIds, c.id]);
                      }}
                    >
                      {isKey ? <span className="cp-rc-key-tip">정답</span> : null}
                      {i + 1}
                    </button>
                  ) : (
                    <span className="cp-rc-choice-num cp-choice-swatch" style={communicationChoiceSwatchStyle(i)} aria-hidden>
                      {i + 1}
                    </span>
                  )}
                  <input
                    className="cp-rc-choice-input"
                    value={c.text}
                    maxLength={REMOTE_CHOICE_MAXLEN}
                    placeholder={`보기 ${i + 1}`}
                    aria-label={`보기 ${i + 1}`}
                    disabled={disabled}
                    onChange={(e) =>
                      setChoices((prev) => prev.map((x) => (x.id === c.id ? { ...x, text: e.target.value } : x)))
                    }
                  />
                  {choices.length > REMOTE_CHOICE_MIN ? (
                    <button
                      type="button"
                      className="cp-rc-choice-remove"
                      aria-label={`보기 ${i + 1} 삭제`}
                      onClick={() => {
                        setChoices((prev) => prev.filter((x) => x.id !== c.id));
                        if (keyChoiceIds.includes(c.id)) {
                          const next = keyChoiceIds.filter((id) => id !== c.id);
                          setKeyChoiceIds(next);
                          if (!next.length && isAnswerMode) setQMode('submit');
                        }
                      }}
                    >
                      ×
                    </button>
                  ) : null}
                </div>
              );
            })}
            <div className="cp-rc-choice-foot">
              {choices.length < REMOTE_CHOICE_MAX ? (
                <div className="cp-rc-choice-row cp-rc-choice-add-row">
                  <span className="cp-rc-choice-handle is-dummy" aria-hidden>
                    ⋮⋮
                  </span>
                  <span className="cp-rc-choice-num is-dummy" aria-hidden>
                    +
                  </span>
                  <button type="button" className="cp-rc-choice-add" onClick={() => setChoices((prev) => [...prev, newChoice()])}>
                    보기 추가
                  </button>
                  {choices.length > REMOTE_CHOICE_MIN ? <span className="cp-rc-choice-remove is-dummy" aria-hidden /> : null}
                </div>
              ) : null}
              {choiceError && text.trim() ? <span className="cp-rc-choice-err">{choiceError}</span> : null}
            </div>
          </div>
        ) : null}
        {keyError && text.trim() && !choiceError ? <p className="cp-rc-choice-err cp-rc-key-err">{keyError}</p> : null}
      </div>

      {/* 음악 바 (소통 프레젠에서는 숨김) */}
      {selIsComm ? null : (
      <div className="cp-rc-music">
        {selIsLesson && ctl.selectedOnline ? (
          <div className="cp-rc-slidenav" role="group" aria-label="슬라이드 이동">
            <button type="button" className="cp-rc-music-btn cp-rc-slidenav-btn" onClick={() => ctl.slideNav('prev')} aria-label="이전 슬라이드">
              ◀
            </button>
            <span className="cp-rc-slidenav-pos" aria-live="polite" title={ctl.slideState?.label}>
              {ctl.slideState ? `${ctl.slideState.index + 1}/${ctl.slideState.total}` : '–'}
            </span>
            <button type="button" className="cp-rc-music-btn cp-rc-slidenav-btn" onClick={() => ctl.slideNav('next')} aria-label="다음 슬라이드">
              ▶
            </button>
          </div>
        ) : null}
        <button type="button" className="cp-rc-music-pick" onClick={() => setMusicOpen(true)} aria-label="배경음악 고르기">
          <RemoteFlatIcon kind="bgm" tone="white" size={24} />
          <span className="cp-rc-music-label">
            {pick?.label ?? '배경음악'}
            <small>
              {music ? `${music.label} · ${remoteEntryStatusText(music)}` : '눌러서 분위기·트랙 선택'}
            </small>
          </span>
        </button>
        <button type="button" className="cp-rc-music-btn" disabled={disabled} onClick={onMusicPlay} aria-label="재생">
          <RemoteFlatIcon kind="play" tone="white" size={20} plain />
        </button>
        <button type="button" className="cp-rc-music-btn" disabled={disabled} onClick={onMusicPause} aria-label="일시정지">
          <RemoteFlatIcon kind="pause" tone="white" size={20} plain />
        </button>
        <button type="button" className="cp-rc-music-btn" disabled={disabled} onClick={onMusicStop} aria-label="정지">
          <RemoteFlatIcon kind="stop" tone="white" size={20} plain />
        </button>
      </div>
      )}

      {targetOpen ? (
        <Sheet title="제어할 프레젠테이션" label="제어할 프레젠테이션 선택" size="lg" onClose={() => setTargetOpen(false)}>
          {comm.available ? (
            <ul className="cp-remote-list cp-rc-target-list cp-rc-comm-target" role="radiogroup" aria-label="개별 디바이스 프레젠테이션">
              <li className={`cp-remote-item${selIsComm ? ' is-selected' : ''}${comm.canUse ? '' : ' is-locked'}`}>
                <button type="button" role="radio" aria-checked={selIsComm} className="cp-remote-item-main" onClick={pickComm}>
                  <span className="cp-remote-radio cp-rc-target-check" aria-hidden>
                    {selIsComm ? '✓' : null}
                  </span>
                  <span className="cp-remote-item-text">
                    <span className="cp-remote-item-name">
                      개별 디바이스 프레젠테이션{comm.room ? ` · ${comm.room.className}` : ''}
                    </span>
                    <span className="cp-remote-item-meta">
                      <span className="cp-rc-target-tag cp-rc-target-tag--comm">휴대용 디바이스</span>
                      <span className="cp-rc-target-meta-text">
                        {!comm.canUse
                          ? '유료 이용권 전용 · 눌러서 안내 보기'
                          : comm.room
                            ? `열림 · 입장번호 ${comm.room.entryCode}`
                            : '눌러서 만들기 · 최대 30명 · 6시간'}
                      </span>
                    </span>
                  </span>
                </button>
                {comm.room && comm.canUse ? (
                  <button
                    type="button"
                    className="cp-rc-target-edit"
                    onClick={() => {
                      setTargetOpen(false);
                      setCommSheet(true);
                    }}
                    title="링크·입장번호"
                    aria-label="개별 디바이스 프레젠테이션 정보"
                  >
                    i
                  </button>
                ) : null}
              </li>
            </ul>
          ) : null}
          {ctl.presentations.length === 0 ? (
            <div className="cp-remote-empty">
              열려 있는 메인 프레젠테이션이 없어요.
              <br />
              같은 계정으로 PC에서 메인 프레젠테이션을 열어 주세요.
            </div>
          ) : (
            <ul className="cp-remote-list cp-rc-target-list" role="radiogroup" aria-label="제어할 프레젠테이션">
              {ctl.presentations.map((p) => {
                const selected = p.sessionId === ctl.selectedSessionId;
                return (
                  <li key={p.sessionId} className={`cp-remote-item${selected ? ' is-selected' : ''}`}>
                    <button
                      type="button"
                      role="radio"
                      aria-checked={selected}
                      className="cp-remote-item-main"
                      onClick={() => {
                        ctl.select(p.sessionId);
                        setTargetOpen(false);
                      }}
                    >
                      <span className="cp-remote-radio cp-rc-target-check" aria-hidden>
                        {selected ? '✓' : null}
                      </span>
                      <span className="cp-remote-item-text">
                        <span className="cp-remote-item-name">{p.name}</span>
                        <span className="cp-remote-item-meta">
                          {p.presentationKind === 'lesson' ? (
                            <span className="cp-rc-target-tag cp-rc-target-tag--lesson">{p.isLocal ? '수업 · 로컬' : '수업 · 온라인'}</span>
                          ) : p.isLocal ? (
                            <span className="cp-rc-target-tag">로컬</span>
                          ) : (
                            <span className="cp-rc-target-tag is-online">● 온라인</span>
                          )}
                          <span className="cp-rc-target-meta-text">
                            {p.presentationKind === 'lesson' ? '수업 프레젠' : p.isLocal ? '이 기기' : '다른 기기'} · {hhmm(p.since)}부터
                          </span>
                        </span>
                      </span>
                    </button>
                    {p.presentationKind === 'lesson' ? null : (
                      <button
                        type="button"
                        className="cp-rc-target-edit"
                        onClick={() => onRename(p)}
                        title="이름 변경"
                        aria-label="이름 변경"
                      >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                          <path d="M12 20h9" />
                          <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L7 19l-4 1 1-4Z" />
                        </svg>
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {ctl.selectedSessionId && !ctl.selectedOnline && !selIsComm ? (
            <p className="cp-remote-cmd-status is-fail cp-remote-target-offline-msg">선택한 프레젠테이션이 오프라인입니다. 다른 프레젠테이션을 선택해 주세요.</p>
          ) : null}
        </Sheet>
      ) : null}

      {commSheet ? (
        <Sheet size="md" fit title="개별 디바이스 프레젠테이션" label="개별 디바이스 프레젠테이션" onClose={() => setCommSheet(false)}>
          <RemoteCommRoomCard
            room={comm.room}
            defaultClassName={commDefaultClass}
            onCreate={async (input) => {
              const err = await comm.create(input);
              if (!err) selectCommOnCreateRef.current = true;
              return err;
            }}
            roster={
              <section className="cp-rc-comm-roster" aria-label="참여 학생">
              <h3 className="cp-rc-comm-roster-title">
                참여 학생 {comm.members.length}/{COMM_MAX_STUDENTS}
                <small> · 접속 {commOnline}</small>
              </h3>
              <div className="cp-rc-comm-members">
                {comm.members.length ? (
                  comm.members.map((m) => (
                    <span key={m.memberId} className={`cp-rc-comm-member${m.online ? ' is-online' : ''}`}>
                      {m.nickname}
                    </span>
                  ))
                ) : (
                  <span className="cp-rc-comm-empty">아직 들어온 학생이 없어요. 링크·입장번호를 알려 주세요.</span>
                )}
              </div>
            </section>
            }
            onEnd={async () => {
              const ok = await comm.end();
              if (ok) {
                setCommSheet(false);
                if (selIsComm) ctl.select(null);
              }
              return ok;
            }}
          />
        </Sheet>
      ) : null}

      {settingsOpen ? (
        <Sheet
          size="md"
          title={
            <>
              <RemoteFlatIcon kind="speaker" size={20} />
              읽어주기 설정
            </>
          }
          label="읽어주기 설정"
          onClose={() => setSettingsOpen(false)}
        >
          <RemoteTtsSettingsCard ctl={ctl} disabled={disabled} busy={pending('tts')} embedded />
          {ttsEntry ? (
            <p className={`cp-remote-cmd-status is-${ttsEntry.state}`} role="status">
              {ttsEntry.label}: {remoteEntryStatusText(ttsEntry)}
            </p>
          ) : null}
        </Sheet>
      ) : null}

      {musicOpen ? (
        <Sheet
          title={
            <>
              <RemoteFlatIcon kind="bgm" size={20} />
              배경음악
            </>
          }
          label="배경음악 선택"
          onClose={() => setMusicOpen(false)}
        >
          {BGM_MOOD_SECTIONS.map((section) => (
            <div key={section.id} className="cp-rc-mood" role="group" aria-label={`${section.label} 트랙`}>
              <span className="cp-rc-mood-title">{section.label}</span>
              <div className="cp-rc-mood-tracks">
                <button
                  type="button"
                  className="cp-rc-chip"
                  disabled={disabled || pending('music')}
                  onClick={() => playPick({ moodId: section.id, mode: 'random' }, `${section.label} 랜덤 플레이`)}
                >
                  랜덤
                </button>
                {slideBgmTracksForMood(section.id).map((track) => (
                  <button
                    key={track}
                    type="button"
                    className="cp-rc-chip"
                    disabled={disabled || pending('music')}
                    onClick={() => playPick({ moodId: section.id, mode: 'track', track }, `${section.label} ${track}`)}
                  >
                    {track}
                  </button>
                ))}
              </div>
            </div>
          ))}
          {music ? (
            <p className="cp-rc-sheet-foot" role="status">
              <b className={`is-${music.state}`}>
                {music.label}: {remoteEntryStatusText(music)}
              </b>
            </p>
          ) : null}
        </Sheet>
      ) : null}
    </div>
  );
};

export default RemoteControllerChat;
